import React from 'react';
import { Mode } from '@src/constants';
import { Grid, Modal, Form, Button, Message } from 'semantic-ui-react';
import { Alert } from '@mui/material';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import ButtonWrapper from '../../common/ButtonWrapper';
import { Loading } from 'react-lib/dist/components-es';
import GridColumnInputWrapper from '../../common/GridColumnInputWrapper';
import { changesNotificationModalActions } from '../../../behavior/changesNotificationModal';
import { Formik } from 'formik';
import PersonListContainer from '../../common/PersonListContainer';
import Person from '../../../objects/Person';
import { InputWrapper, MarkDownEditorComponent } from 'react-lib/dist/components-es';
import { FILES_TOTAL_SIZE_LIMIT } from '../../../constants';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import Inventory from '@src/objects/Inventory';
import { connect } from 'react-redux';
import SkippedInventoriesAlert from '../../BatchUpdate/SkippedInventoriesAlert';

type SkippedInventory = {
  id: number;
  displayName: string;
  reason: 'WOULD_LEAVE_NO_CONTACTS' | 'NO_PERMISSION';
};

type Props = {
  toggleChangesNotificationModal: (arg: boolean) => void;
  open: boolean;
  mode: Mode;
  isLoading: boolean;
  processedChangesNotification: string;
  recipients: PersonDTO[];
  submitForm: () => void;
  disabled: boolean;
  externalTrigger: boolean;
  warning: string;
  inventory: Inventory;
  showPersonalData: boolean;
  updateProcessedChangesNotification: (arg1: string, arg2: PersonDTO[]) => void;
  skippedInventories?: SkippedInventory[];
  batchAllNoOp?: boolean;
  contactBatchMode?: 'ADD' | 'REMOVE' | 'REPLACE' | 'OVERRIDE';
};

const ChangesNotificationModal = ({
  toggleChangesNotificationModal,
  open,
  mode,
  isLoading,
  processedChangesNotification,
  recipients,
  submitForm,
  disabled,
  externalTrigger,
  warning,
  inventory,
  showPersonalData,
  updateProcessedChangesNotification,
  skippedInventories,
  batchAllNoOp,
  contactBatchMode,
}: Props) => {
  const { t } = useTranslation();

  const translatedLabel = t(`details.page.save.button`);
  const invJsonString = JSON.stringify(inventory);
  const sizeInBytes = invJsonString?.length;
  const toggleChangesNotificationModalOrSaveImmediately = () => {
    submitForm();
  };

  const getFinalEmailNotificationBody = (values: {
    processedChangesNotification: string;
    processedChangesNotificationComments: string;
  }) => {
    let finalEmailNotificationBody =
      values.processedChangesNotification +
      (values.processedChangesNotificationComments
        ? '\n' + values.processedChangesNotificationComments
        : '');
    return finalEmailNotificationBody;
  };

  const getLoadingChild = () => {
    if (sizeInBytes && sizeInBytes > 10000) {
      return t('save.loader.size.message', {
        size: '~' + (sizeInBytes / 1e6).toFixed(2) + ' mb',
      });
    }
    return null;
  };

  const formatProcessedChangesNotification = (
    processedChangesNotification: string,
    showPersonalData: boolean
  ) => {
    if (processedChangesNotification && !showPersonalData) {
      const regexUser = /user :[\d\D]*?\/br>/g;
      const regexUserContacts = /Contacts:[\d\D]*?\/br>/g;

      return processedChangesNotification
        .replaceAll(regexUser, 'user : XXXX -> XXXX</br>')
        .replaceAll(regexUserContacts, 'Contacts : XXXX -> XXXX</br>');
    }
    return processedChangesNotification;
  };

  return (
    <Modal
      trigger={
        !externalTrigger ? (
          <ButtonWrapper
            key={translatedLabel}
            icon="save"
            type="button"
            label={translatedLabel}
            primary={true}
            onClick={() => toggleChangesNotificationModalOrSaveImmediately()}
            className="mini right floated"
            disabled={disabled}
          />
        ) : null
      }
      centered={true}
      open={open}
      onClose={() => (!isLoading ? toggleChangesNotificationModal(false) : null)}
      size="large"
      closeIcon={!isLoading}
    >
      <Modal.Header>{t(`save.modal.${mode}.title`)}</Modal.Header>
      {processedChangesNotification && sizeInBytes < FILES_TOTAL_SIZE_LIMIT && (
        <Modal.Content>
          <Loading isLoading={isLoading} children={getLoadingChild()} />
          <SkippedInventoriesAlert skippedInventories={skippedInventories} />
          {batchAllNoOp && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                contactBatchMode === 'REPLACE'
                  ? 'batch.update.contacts.diff.empty.state.replace'
                  : contactBatchMode === 'REMOVE'
                  ? 'batch.update.contacts.diff.empty.state.remove'
                  : 'batch.update.contacts.diff.empty.state'
              )}
            </Alert>
          )}
          <Formik
            initialValues={{
              processedChangesNotification: formatProcessedChangesNotification(
                processedChangesNotification,
                showPersonalData
              ),
              recipients,
              processedChangesNotificationComments: '',
            }}
            render={formikProps => (
              <Form>
                <Grid
                  verticalAlign="top"
                  columns={2}
                  className="containerReducedWhiteSpace changesNotificationRecipients"
                >
                  <Grid.Column width={10}>
                    <Grid className="containerInContainerReducedWhiteSpace">
                      <GridColumnInputWrapper width={3} label="save.modal.notification.body" />
                      <GridColumnInputWrapper
                        width={13}
                        render={(props: any) => (
                          <MarkDownEditorComponent
                            {...props}
                            name="processedChangesNotification"
                            canEdit={false}
                            {...formikProps}
                          />
                        )}
                      />
                      <GridColumnInputWrapper width={3} label="save.modal.notification.comments" />
                      <GridColumnInputWrapper
                        width={13}
                        name="processedChangesNotificationComments"
                        render={(props: any) => (
                          <InputWrapper {...props} {...formikProps} type="textArea" />
                        )}
                      />
                    </Grid>
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Grid className="containerInContainerReducedWhiteSpace">
                      {warning && (
                        <Grid.Column width={16}>
                          <Message color="yellow">{t(warning)}</Message>
                        </Grid.Column>
                      )}
                      <Grid.Column width={16}>
                        <Message color="yellow">
                          {t('save.modal.notification.recipients.warning')}
                        </Message>
                      </Grid.Column>
                      <GridColumnInputWrapper
                        {...formikProps}
                        width={16}
                        label="save.modal.notification.recipients"
                        name="changesNotificationRecipients"
                        fieldClassName="containerReducedWhiteSpace"
                        render={(props: any) => (
                          <PersonListContainer
                            {...props}
                            {...formikProps}
                            validateOnChange={false}
                            newPersonEntity={Person}
                            arrayPropName="recipients"
                            showAddNew={true}
                            showDeleteHandler={true}
                            showNoEmailAvailable={true}
                            showRole={true}
                          />
                        )}
                      />
                    </Grid>
                  </Grid.Column>
                  <Grid.Column width={10} />
                  <Grid.Column width={6} style={{ marginTop: '15px' }}>
                    <Button.Group className="mini right floated">
                      <ButtonWrapper
                        type="button"
                        label={t(`cancel.button`)}
                        onClick={() => toggleChangesNotificationModal(false)}
                        color="red"
                      />
                      <ButtonWrapper
                        key={translatedLabel}
                        type="button"
                        label={translatedLabel}
                        onClick={() => {
                          updateProcessedChangesNotification(
                            getFinalEmailNotificationBody(formikProps.values),
                            formikProps.values.recipients
                          );
                          submitForm();
                        }}
                        primary={true}
                        disabled={batchAllNoOp}
                      />
                    </Button.Group>
                  </Grid.Column>
                </Grid>
              </Form>
            )}
            onSubmit={submitForm}
          />
        </Modal.Content>
      )}
      {sizeInBytes >= FILES_TOTAL_SIZE_LIMIT && (
        <Modal.Content>
          <Message color="yellow">
            {t('save.modal.notification.total.files.size.error', {
              actual: (sizeInBytes / 1e6).toFixed(2),
              limit: (FILES_TOTAL_SIZE_LIMIT / 1e6).toFixed(2),
            })}
          </Message>
        </Modal.Content>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  ...state.changesNotificationModal,
  showPersonalData: state.dataPrivacy.showPersonalData,
  contactBatchMode: state.batchUpdate?.contactBatchMode,
});

const mapDispatchToProps = (dispatch: any) => ({
  toggleChangesNotificationModal: (state: boolean) =>
    dispatch(changesNotificationModalActions.toggle(state)),
  updateProcessedChangesNotification: (
    processedChangesNotification: string,
    recipients: PersonDTO[]
  ) =>
    dispatch(
      changesNotificationModalActions.updateProcessedChangesNotification(
        processedChangesNotification,
        recipients
      )
    ),
});

ChangesNotificationModal.defaultProps = {
  externalTrigger: false,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(ChangesNotificationModal);
